import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Plus, Search, X } from '@/components/icons';
import { DEFAULT_GROUP_PARALLEL, MAX_GROUP_PARALLEL, testNameKey, type GroupMode } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import type { TestCase, TestGroup, TestGroupInput } from '@/lib/types';
import { Button, Dialog, Field, Input, Select, Textarea, Toggle, useToast } from '@/components/ui';

const MODE_OPTIONS: { value: GroupMode; title: string; description: string }[] = [
  { value: 'SEQUENTIAL', title: 'One after another', description: 'In the order below. Best when tests depend on each other or share a test account.' },
  { value: 'PARALLEL', title: 'At the same time', description: 'Several tests side by side - faster. Tests must not get in each other’s way.' },
];

const PARALLEL_OPTIONS = Array.from({ length: MAX_GROUP_PARALLEL - 1 }, (_, i) => {
  const n = i + 2;
  return { value: String(n), label: `${n} tests at a time` };
});

/** Create or edit a group: name, how it runs, and its tests in run order. */
export function GroupDialog({
  projectId,
  tests,
  groups,
  group,
  open,
  onClose,
}: {
  projectId: string;
  tests: TestCase[];
  /** Other groups of the project, to warn about a taken name before saving. */
  groups: TestGroup[];
  /** Edit this group; omit to create one. */
  group?: TestGroup;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [mode, setMode] = useState<GroupMode>(group?.mode ?? 'SEQUENTIAL');
  const [maxParallel, setMaxParallel] = useState(group?.maxParallel ?? DEFAULT_GROUP_PARALLEL);
  const [stopOnFailure, setStopOnFailure] = useState(group?.stopOnFailure ?? false);
  const [selected, setSelected] = useState<string[]>(group?.tests.map((t) => t.id).filter((id) => tests.some((t) => t.id === id)) ?? []);
  const [query, setQuery] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const byId = new Map(tests.map((t) => [t.id, t]));
  const nameTaken = !!name.trim() && groups.some((g) => g.id !== group?.id && testNameKey(g.name) === testNameKey(name));
  const available = tests.filter((t) => !selected.includes(t.id) && (!query.trim() || t.name.toLowerCase().includes(query.trim().toLowerCase())));
  const problems = [
    ...(name.trim() ? [] : ['Give the group a name.']),
    ...(nameTaken ? [`A group named “${name.trim()}” already exists in this project.`] : []),
    ...(selected.length ? [] : ['Add at least one test.']),
  ];

  const save = useMutation({
    mutationFn: () => {
      const body: TestGroupInput = {
        name: name.trim(),
        description: description.trim() || null,
        mode,
        maxParallel,
        stopOnFailure,
        testCaseIds: selected,
      };
      return group ? api.updateGroup(group.id, body) : api.createGroup(projectId, body);
    },
    onSuccess: (g) => {
      toast.success(group ? 'Group saved' : `Group “${g.name}” created`);
      qc.invalidateQueries({ queryKey: ['groups', projectId] });
      onClose();
    },
    onError: (e) => {
      toast.error(errorMessage(e));
      qc.invalidateQueries({ queryKey: ['groups', projectId] });
    },
  });

  const move = (index: number, by: -1 | 1) =>
    setSelected((s) => {
      const n = [...s];
      const [item] = n.splice(index, 1);
      n.splice(index + by, 0, item!);
      return n;
    });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (problems.length) {
      setShowErrors(true);
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={group ? 'Edit group' : 'New test group'}
      description="Run several tests together - one after another or at the same time."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="group-form" loading={save.isPending}>
            {group ? 'Save group' : 'Create group'}
          </Button>
        </>
      }
    >
      <form id="group-form" onSubmit={submit} className="space-y-5">
        <Field label="Name" error={nameTaken ? `A group named “${name.trim()}” already exists in this project. Choose a different name.` : showErrors && !name.trim() ? 'Give the group a name.' : undefined}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Checkout smoke" maxLength={200} autoFocus />}
        </Field>
        <Field label="Description (optional)">
          {(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={2000} />}
        </Field>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-700 dark:text-slate-200">How should the tests run?</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {MODE_OPTIONS.map((o) => (
              <label
                key={o.value}
                className={clsx(
                  'flex cursor-pointer gap-3 rounded-xl p-3 ring-1 transition',
                  mode === o.value ? 'bg-brand-50 ring-brand-400 dark:bg-brand-950/40 dark:ring-brand-700' : 'ring-slate-200 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800',
                )}
              >
                <input type="radio" name="group-mode" className="mt-1 accent-primary" checked={mode === o.value} onChange={() => setMode(o.value)} />
                <span>
                  <span className="block text-sm font-medium">{o.title}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{o.description}</span>
                </span>
              </label>
            ))}
          </div>
          {mode === 'PARALLEL' && (
            <Field label="Tests at the same time" hint="Each test is its own browser. The server may run fewer at once if it is busy.">
              {(id) => (
                <Select id={id} wrapperClassName="w-56" value={String(maxParallel)} onChange={(e) => setMaxParallel(Number(e.target.value))} options={PARALLEL_OPTIONS} />
              )}
            </Field>
          )}
        </fieldset>

        <div className="flex items-start gap-3">
          <Toggle checked={stopOnFailure} onChange={setStopOnFailure} label="Stop at the first failure" />
          <div className="text-sm">
            <div className="font-medium text-slate-700 dark:text-slate-200">Stop at the first failure</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Tests that have not started yet are reported as not run. Tests already running finish.</div>
          </div>
        </div>

        <section aria-labelledby="group-tests-title" className="space-y-2">
          <h3 id="group-tests-title" className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Tests in this group {selected.length > 0 && <span className="text-slate-400">({selected.length})</span>}
          </h3>
          {selected.length === 0 ? (
            <p className={clsx('rounded-xl border border-dashed p-4 text-center text-sm', showErrors ? 'border-rose-300 text-rose-600' : 'border-slate-200 text-slate-500 dark:border-slate-700')}>
              Add tests from the list below.
            </p>
          ) : (
            <ol className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
              {selected.map((id, i) => (
                <li key={id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="w-6 text-right text-xs text-slate-400">{mode === 'SEQUENTIAL' ? `${i + 1}.` : '•'}</span>
                  <span className="min-w-0 flex-1 truncate">{byId.get(id)?.name ?? 'Deleted test'}</span>
                  {mode === 'SEQUENTIAL' && (
                    <>
                      <Button type="button" size="sm" variant="ghost" aria-label={`Move ${byId.get(id)?.name} up`} disabled={i === 0} onClick={() => move(i, -1)} icon={<ArrowUp className="size-4" />} />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`Move ${byId.get(id)?.name} down`}
                        disabled={i === selected.length - 1}
                        onClick={() => move(i, 1)}
                        icon={<ArrowDown className="size-4" />}
                      />
                    </>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${byId.get(id)?.name} from the group`}
                    onClick={() => setSelected((s) => s.filter((x) => x !== id))}
                    icon={<X className="size-4 text-rose-500" />}
                  />
                </li>
              ))}
            </ol>
          )}
        </section>

        <section aria-labelledby="group-add-title" className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="group-add-title" className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Add tests
            </h3>
            <div className="relative ml-auto w-full sm:w-60">
              <Search className="pointer-events-none absolute top-1/2 left-4 size-3.5 -translate-y-1/2 text-fg-muted" />
              <Input aria-label="Search tests to add" placeholder="Search tests…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-10" />
            </div>
            {available.length > 1 && (
              <Button type="button" size="sm" variant="outline" onClick={() => setSelected((s) => [...s, ...available.map((t) => t.id)])}>
                Add all {available.length}
              </Button>
            )}
          </div>
          {available.length === 0 ? (
            <p className="text-sm text-slate-500">{tests.length === 0 ? 'This project has no tests yet.' : query ? 'No tests match.' : 'Every test is in the group.'}</p>
          ) : (
            <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-xl ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
              {available.map((t) => (
                <li key={t.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">
                    {t.name}
                    {t.createdBy && <span className="text-xs text-slate-400"> · by {t.createdBy.name}</span>}
                  </span>
                  <Button type="button" size="sm" variant="ghost" aria-label={`Add ${t.name}`} onClick={() => setSelected((s) => [...s, t.id])} icon={<Plus className="size-4" />} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {showErrors && problems.length > 0 && (
          <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">
            {problems[0]}
          </p>
        )}
      </form>
    </Dialog>
  );
}
